import { motion } from 'motion/react';

export interface ReelErrorMessageProps {
  headline: string;
  body: string;
  /** Plinth art — rendered faintly behind the message so the stage reads as
   *  "empty pedestal, no card", matching the winner scene. */
  pedestalSrc: string;
}

/**
 * Center-stage content shown when the next-pack purchase fails. By the time
 * this is visible the reel tiles have faded to an empty pedestal; this fades
 * in an alert + copy where the card would have landed. Presentational only —
 * the Try Again / Back actions live in the reel's bottom row. Rendered inside
 * the reel's relative scene box (absolute fill).
 */
export function ReelErrorMessage({ headline, body, pedestalSrc }: ReelErrorMessageProps) {
  return (
    <>
      {/* Faint empty plinth, bottom-centred like the winner pedestal. */}
      <img
        src={pedestalSrc}
        alt=""
        aria-hidden
        draggable={false}
        className="pointer-events-none absolute bottom-0 left-1/2 z-10 w-[60%] max-w-none -translate-x-1/2 select-none opacity-40"
      />
      <motion.div
        className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 px-6 text-center pointer-events-none"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut', delay: 0.15 }}
      >
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full"
          style={{
            background: 'var(--co-ReelErrorMessage-iconBg, rgba(239,68,68,0.15))',
            color: 'var(--co-ReelErrorMessage-iconColor, #EF4444)',
          }}
          aria-hidden
        >
          <svg
            aria-hidden
            width="34"
            height="34"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h2
          className="type-headline font-bold"
          style={{ color: 'var(--co-WinnerStepTitle-color, #FFFFFF)' }}
        >
          {headline}
        </h2>
        <p className="type-subhead max-w-[320px] opacity-80">{body}</p>
      </motion.div>
    </>
  );
}
